import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { VENTURES, publishedVentures } from "@/data/site";
import { PageHero, SectionHead, CtaBand, BulletList } from "@/components/Blocks";
import VenturesBento from "@/components/home/VenturesBento";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants, MaskLines, EASE } from "@/components/motion";
import { StatusBadge, LogoPlate, InterfaceGlyph } from "@/components/VentureBits";
import { Sparkle } from "@/components/Art";
import { useSeo } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const SELECTION = [
  ["A real, specific problem", "We start from a problem we understand deeply — often one we've experienced building software ourselves."],
  ["A small first version", "Each venture launches with a focused first release and earns the right to grow."],
  ["Independent brand", "Every product keeps its own identity, positioning, and website."],
  ["Shared engineering backbone", "Ventures benefit from SparkIdea's product, design, and engineering experience."],
];

export function VenturesPage() {
  useSeo("Ventures", "Independent software products built by SparkIdea Interactive: Nautis, DemoVexa, Qikod, and Zingura.");
  return (
    <>
      <PageHero testId="ventures-hero" eyebrow="Ventures" title={["Independent ideas.", "Built into real products."]}
        intro="SparkIdea Interactive creates and grows its own software ventures. Each one is built around a specific problem, a clear audience, and a focused ambition." />
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHead eyebrow="Venture-building philosophy" title={["Products first.", "Hype never."]} /></div>
          <Reveal delay={0.1} className="lead lg:col-span-7">
            We build ventures the same way we'd advise any founder to: understand the problem, ship a focused first version, learn from real users, and grow deliberately. Building our own products keeps our engineering judgment sharp — and it's the clearest evidence of how we work.
          </Reveal>
        </div>
      </section>
      <VenturesBento withHead={false} />
      <section className="border-t border-si-line bg-si-bg2 py-24 sm:py-32">
        <div className="wrap">
          <SectionHead eyebrow="How new ventures are developed" title="From spark to standalone product." />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SELECTION.map(([t, d], i) => (
              <motion.div key={t} variants={itemVariants} className="panel p-7">
                <span className="font-mono text-xs text-si-cyan">0{i + 1}</span>
                <h3 className="mt-6 text-xl font-bold tracking-tight">{t}</h3>
                <p className="mt-3 text-base leading-relaxed text-si-muted">{d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
      <CtaBand testId="ventures-cta" title="Have an idea worth building together?" intro="We selectively partner with founders to explore, validate, and co-build new ventures." primary="Explore Partnerships" primaryTo="/venture-partnerships" secondary="Start a Project" secondaryTo="/contact" />
    </>
  );
}

export function VentureDetail() {
  const { slug } = useParams();
  const v = VENTURES.find((x) => x.slug === slug && x.published);
  useSeo(v ? `${v.name} — ${v.category}` : "Not found", v?.shortDescription);
  if (!v) return <NotFound />;
  const others = publishedVentures().filter((x) => x.slug !== v.slug);
  const live = v.websiteUrl && v.status === "Live";
  return (
    <>
      <section className="relative overflow-hidden pb-20 pt-36 sm:pt-44" data-testid={`venture-detail-${v.slug}`}>
        <div className="pointer-events-none absolute -left-40 top-0 h-[600px] w-[600px] rounded-full blur-[120px]" style={{ background: `${v.accent}2e` }} />
        <div className="pointer-events-none absolute -right-40 bottom-0 h-[400px] w-[500px] rounded-full blur-[120px]" style={{ background: `${v.accent2}22` }} />
        <div className="wrap relative">
          <Link to="/ventures" className="inline-flex items-center gap-2 text-sm font-semibold text-si-muted hover:text-si-text" data-testid="venture-back"><ArrowLeft className="h-4 w-4" />All ventures</Link>
          <div className="mt-10 grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <div className="flex flex-wrap items-center gap-3"><span className="eyebrow">{v.category}</span><StatusBadge status={v.status} /></div>
              <MaskLines as="h1" onLoad lines={[v.name]} className="mt-6 text-[clamp(3.2rem,9vw,7.5rem)] font-extrabold leading-[0.95] tracking-[-0.045em]" />
              <Reveal delay={0.2} className="mt-8 max-w-xl text-lg leading-relaxed text-si-muted sm:text-xl">{v.shortDescription}</Reveal>
              <Reveal delay={0.35} className="mt-10 flex flex-wrap gap-3">
                {live ? <Cta href={v.websiteUrl} external size="lg" testId="venture-visit" trackId={`visit-${v.slug}`}>Visit {v.name}</Cta>
                  : <span className="inline-flex h-14 items-center rounded-xl border border-si-line px-6 text-[15px] font-semibold text-si-muted">{v.name} is in development</span>}
                <Cta to="/contact" variant="secondary" size="lg" testId="venture-contact">Talk to Us</Cta>
              </Reveal>
            </div>
            <motion.div initial={{ opacity: 0, y: 40, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 1.1, ease: EASE, delay: 0.2 }} className="lg:col-span-6">
              <div className="grid gap-4">
                <LogoPlate v={v} className="h-48 sm:h-60" />
                <div className="h-56"><InterfaceGlyph v={v} /></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHead eyebrow="About the product" title={`Why ${v.name} exists.`} /></div>
          <div className="space-y-8 lg:col-span-7">
            <Reveal className="lead">{v.longDescription}</Reveal>
            {v.problem && <Reveal className="panel p-7"><p className="eyebrow">The problem</p><p className="mt-3 text-lg text-si-text">{v.problem}</p></Reveal>}
            {v.audience && <Reveal className="panel p-7"><p className="eyebrow">Who it's for</p><p className="mt-3 text-lg text-si-text">{v.audience}</p></Reveal>}
            {v.capabilities.length > 0 && <Reveal><p className="eyebrow mb-5">Key capabilities</p><BulletList items={v.capabilities} /></Reveal>}
          </div>
        </div>
      </section>
      <section className="border-t border-si-line bg-si-bg2 py-20">
        <div className="wrap grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-4 flex items-center gap-2"><Sparkle className="h-3 w-3" />A SparkIdea Interactive venture</p>
            <p className="text-xl leading-relaxed text-si-text sm:text-2xl">{v.name} is an independent product created by SparkIdea Interactive, with its own brand and roadmap. Status: <span className="font-semibold">{v.status}</span>.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">{live && <Cta href={v.websiteUrl} external variant="secondary" testId="venture-visit-2">Go to {v.websiteUrl.replace("https://", "")}</Cta>}</div>
        </div>
      </section>
      <section className="py-20">
        <div className="wrap">
          <p className="eyebrow mb-8">More ventures</p>
          <div className="grid gap-4 sm:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/ventures/${o.slug}`} className="group panel flex items-center justify-between p-6 transition-colors hover:border-si-cyan/50" data-testid={`other-venture-${o.slug}`}>
                <span><span className="block text-xl font-bold">{o.name}</span><span className="text-sm text-si-muted">{o.category}</span></span>
                <StatusBadge status={o.status} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
